/**
 * Environment variables utility
 * Loads env.js file at runtime and provides access to environment variables
 */

declare global {
  interface Window {
    __FEEGOW_FRONTEND_ENVS__?: Record<string, string>;
  }
}

// Type declarations for webpack globals
declare const process:
  | {
      env: Record<string, string | undefined>;
    }
  | undefined;

declare const __webpack_require__:
  | {
      p: string;
    }
  | undefined;

let loadPromise: Promise<void> | null = null;

/**
 * Gets the public path from webpack
 */
function getPublicPath(): string {
  if (typeof __webpack_require__ !== "undefined" && __webpack_require__.p) {
    return __webpack_require__.p;
  }
  return "";
}

/**
 * Loads the env.js file using webpack's public path
 * @returns Promise that resolves when env.js is loaded
 */
export function loadEnv(): Promise<void> {
  // If already loaded, return resolved promise
  if (window.__FEEGOW_FRONTEND_ENVS__) {
    return Promise.resolve();
  }

  // If already loading, return existing promise
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise((resolve) => {
    const publicPath = getPublicPath();
    const envJsUrl = `${publicPath}env.js`;

    const script = document.createElement("script");
    script.src = envJsUrl;
    script.async = true;

    script.onload = () => {
      setTimeout(() => {
        resolve();
      }, 10);
    };

    script.onerror = () => {
      resolve(); // Fallback to process.env
    };

    document.head.appendChild(script);
  });

  return loadPromise;
}

/**
 * Gets an environment variable value
 * Note: Call loadEnv() and await it before using getEnv() to ensure env.js is loaded
 * @param key - Environment variable key
 * @param defaultValue - Optional default value if variable is not found
 * @returns Environment variable value or default value
 */
export function getEnv(key: string, defaultValue?: string): string | undefined {
  // Try window first (runtime)
  if (window.__FEEGOW_FRONTEND_ENVS__?.[key] !== undefined) {
    return window.__FEEGOW_FRONTEND_ENVS__[key];
  }

  // Fallback to process.env (build-time)
  if (
    typeof process?.env !== "undefined" &&
    process?.env?.[key] !== undefined
  ) {
    return process.env[key];
  }

  return defaultValue;
}

// Auto-load env.js when module is imported
if (typeof window !== "undefined" && typeof document !== "undefined") {
  loadEnv();
}
